import { PROMOTION_TYPES } from '@/domainLogic/index.ts';
import type { Localization } from '../components/search/common/Localization';

/**
 * Single source of truth mapping periodic promotion types to their label
 * translation keys. Shared by every surface that renders these badges.
 */
export const PERIODIC_PROMOTION_LABEL_KEYS: Partial<Record<PROMOTION_TYPES, string>> = {
  [PROMOTION_TYPES.BLACK_FRIDAY]: 'vacation.offer.blackfriday_uppercase',
  [PROMOTION_TYPES.CYBER_MONDAY]: 'vacation.offer.cybermonday_uppercase',
  [PROMOTION_TYPES.TRAVEL_TUESDAY]: 'vacation.offer.traveltuesday_uppercase',
  [PROMOTION_TYPES.SUMMER_SALE]: 'vacation.offer.summersale_uppercase',
  [PROMOTION_TYPES.AUTUMN_SALES]: 'vacation.offer.autumnsales_uppercase',
};

/**
 * Translated label of a periodic promotion, or '' when it has no mapped key or its copy is
 * missing — so callers can skip the badge instead of rendering an empty pill.
 */
export const getPeriodicPromotionLabel = (promotionType: PROMOTION_TYPES, t: (key: string) => string): string => {
  const labelKey = PERIODIC_PROMOTION_LABEL_KEYS[promotionType];
  return labelKey ? t(labelKey) : '';
};

const getDateFromConfigString = (dateString: unknown): { start: Date | null; end: Date | null } => {
  // example dateString: 2025-11-17,2024-11-29
  if (!dateString || typeof dateString !== 'string') {
    return { start: null, end: null };
  }

  const [startStr, endStr] = dateString.split(',');
  const start = startStr ? new Date(startStr) : null;
  const end = endStr ? new Date(endStr) : null;

  // Set end date to end of day (23:59:59.999) to include the entire day
  if (end) {
    end.setUTCHours(23, 59, 59, 999);
  }

  return { start, end };
};

/**
 * Checks if the given UTC date falls within the period defined by a
 * "start,end" config string (end inclusive to the end of day).
 */
const isWithinPromotionPeriod = (utcDate: string, dateString: unknown): boolean => {
  const now = new Date(utcDate);
  const { start, end } = getDateFromConfigString(dateString);
  return Boolean(start && end && now >= start && now <= end);
};

type PeriodicPromotion = {
  type: PROMOTION_TYPES;
  dateKey: keyof Localization['configValuesApp'];
  /** Only shown for Lastminute Deal hotels, so flash sales keep their own badge elsewhere. */
  requiresLastMinuteDeals?: boolean;
};

const PERIODIC_PROMOTIONS: ReadonlyArray<PeriodicPromotion> = [
  { type: PROMOTION_TYPES.BLACK_FRIDAY, dateKey: 'blackFridayDates' },
  { type: PROMOTION_TYPES.CYBER_MONDAY, dateKey: 'cyberMondayDates' },
  { type: PROMOTION_TYPES.TRAVEL_TUESDAY, dateKey: 'travelTuesdayDates' },
  { type: PROMOTION_TYPES.SUMMER_SALE, dateKey: 'summerSaleDates', requiresLastMinuteDeals: true },
  { type: PROMOTION_TYPES.AUTUMN_SALES, dateKey: 'autumnSalesDates', requiresLastMinuteDeals: true },
];

export const getActivePeriodicPromotion = (utcDate: string, localization: Localization): PROMOTION_TYPES | null => {
  const config = localization?.configValuesApp;
  if (!config?.enablePeriodicPromotions) return null;

  const active = PERIODIC_PROMOTIONS.find(({ dateKey }) => isWithinPromotionPeriod(utcDate, config[dateKey]));
  if (!active) return null;

  if (active.requiresLastMinuteDeals && !config.lastMinuteDeals) return null;
  return active.type;
};
